import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Building2, Users, Briefcase, MessageSquare, LogOut,
  LayoutDashboard, FileText, Eye, Trash2, Clock,
  Home, Hammer, Wrench, ChevronRight, X, Search,
  CheckCircle2, AlertCircle, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const LEAD_LABELS = {
  under_50k: "Under $50,000", "50k_100k": "$50,000 - $100,000", "100k_250k": "$100,000 - $250,000",
  "250k_500k": "$250,000 - $500,000", "500k_1m": "$500,000 - $1,000,000", over_1m: "Over $1,000,000",
  immediate: "Immediate (1-3 months)", soon: "Soon (3-6 months)", planning: "Planning (6-12 months)", future: "Future (12+ months)"
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const adminApi = axios.create();
adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
adminApi.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      toast.error("Your session has ended. Please sign in again.");
      window.location.assign('/admin');
    }
    return Promise.reject(error);
  }
);

const statusOptions = [
  { value: "new", label: "New", color: "bg-blue-100 text-blue-800" },
  { value: "in_progress", label: "In Progress", color: "bg-yellow-100 text-yellow-800" },
  { value: "completed", label: "Completed", color: "bg-green-100 text-green-800" },
  { value: "cancelled", label: "Cancelled", color: "bg-red-100 text-red-800" }
];

const Sidebar = ({ activePage, setActivePage }) => {
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'leads', label: 'Leads', icon: Users },
    { id: 'projects', label: 'Projects', icon: Briefcase },
    { id: 'testimonials', label: 'Testimonials', icon: MessageSquare }
  ];

  return (
    <div className="w-64 bg-slate-900 min-h-screen fixed left-0 top-0 flex flex-col" data-testid="admin-sidebar">
      {/* Logo */}
      <div className="p-6 border-b border-slate-800">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-10 h-10 bg-orange-600 flex items-center justify-center">
            <Building2 className="w-6 h-6 text-white" />
          </div>
          <span className="font-bold text-lg tracking-tight text-white font-['Archivo']">FINIX TERRA</span>
        </Link>
      </div>

      {/* Menu */}
      <nav className="flex-1 p-4">
        <div className="space-y-1">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActivePage(item.id)}
              className={`sidebar-link w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                activePage === item.id 
                  ? 'text-white bg-orange-600/20 border-l-3 border-orange-600' 
                  : 'text-slate-400 hover:text-white'
              }`}
              data-testid={`sidebar-${item.id}`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </button>
          ))}
        </div>
      </nav>

      {/* Logout */}
      <div className="p-4 border-t border-slate-800">
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-slate-400 hover:text-white transition-colors"
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5" />
          Sign Out
        </button>
      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon: Icon, trend }) => (
  <div className="bg-white border border-slate-200 p-6" data-testid={`stat-${title.toLowerCase().replace(' ', '-')}`}>
    <div className="flex items-start justify-between">
      <div>
        <p className="font-mono text-xs uppercase tracking-wider text-slate-500 mb-2">{title}</p>
        <p className="text-3xl font-bold text-slate-900 font-['Archivo']">{value}</p>
      </div>
      <div className="w-12 h-12 bg-slate-100 flex items-center justify-center">
        <Icon className="w-6 h-6 text-orange-600" />
      </div>
    </div>
  </div>
);

const DashboardView = ({ stats }) => (
  <div className="space-y-8" data-testid="dashboard-view">
    <div>
      <h1 className="text-2xl font-bold text-slate-900 mb-2 font-['Archivo'] uppercase">Dashboard</h1>
      <p className="text-slate-500">Overview of your remodeling business</p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <StatCard title="Total Leads" value={stats.total_leads} icon={Users} />
      <StatCard title="New Leads" value={stats.new_leads} icon={AlertCircle} />
      <StatCard title="In Progress" value={stats.in_progress} icon={Clock} />
      <StatCard title="Completed" value={stats.completed} icon={CheckCircle2} />
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <StatCard title="Total Projects" value={stats.total_projects} icon={Briefcase} />
      <StatCard title="Testimonials" value={stats.total_testimonials} icon={MessageSquare} />
    </div>
  </div>
);

const LeadsView = ({ leads, onRefresh }) => {
  const [selectedLead, setSelectedLead] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  const filteredLeads = leads.filter(lead => {
    const matchesStatus = statusFilter === "all" || lead.status === statusFilter;
    const matchesSearch = lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          lead.email.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const handleStatusChange = async (leadId, newStatus) => {
    setIsUpdating(true);
    try {
      await adminApi.patch(`${API}/leads/${leadId}`, { status: newStatus });
      toast.success("Lead status updated");
      onRefresh();
    } catch (error) {
      toast.error("Failed to update status");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async (leadId) => {
    if (!window.confirm("Are you sure you want to delete this lead?")) return;
    
    try {
      await adminApi.delete(`${API}/leads/${leadId}`);
      toast.success("Lead deleted");
      onRefresh();
      setSelectedLead(null);
    } catch (error) {
      toast.error("Failed to delete lead");
    }
  };

  const getServiceList = (serviceType) => {
    const labels = {
      bathroom: "Bathroom", kitchen: "Kitchen", flooring: "Flooring & Tile",
      painting: "Painting", handyman: "Handyman",
      residential: "Residential", commercial: "Commercial", renovation: "Renovation", roofing: "Roofing"
    };
    const services = Object.keys(labels).filter((k) => serviceType?.[k]).map((k) => labels[k]);
    return services.join(", ") || "None";
  };

  const getStatusBadge = (status) => {
    const option = statusOptions.find(o => o.value === status);
    return option ? option.color : "bg-gray-100 text-gray-800";
  };

  return (
    <div className="space-y-6" data-testid="leads-view">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2 font-['Archivo'] uppercase">Leads</h1>
          <p className="text-slate-500">Manage customer inquiries</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 rounded-sm"
            data-testid="search-leads"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 rounded-sm" data-testid="filter-status">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {statusOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-mono text-xs uppercase">Name</TableHead>
              <TableHead className="font-mono text-xs uppercase">Contact</TableHead>
              <TableHead className="font-mono text-xs uppercase">Services</TableHead>
              <TableHead className="font-mono text-xs uppercase">Status</TableHead>
              <TableHead className="font-mono text-xs uppercase">Date</TableHead>
              <TableHead className="font-mono text-xs uppercase">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredLeads.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12 text-slate-500">
                  No leads found
                </TableCell>
              </TableRow>
            ) : (
              filteredLeads.map((lead) => (
                <TableRow key={lead.id} className="table-row-hover" data-testid={`lead-row-${lead.id}`}>
                  <TableCell className="font-medium">{lead.name}</TableCell>
                  <TableCell>
                    <div className="text-sm">{lead.email}</div>
                    <div className="text-xs text-slate-500">{lead.phone}</div>
                  </TableCell>
                  <TableCell className="text-sm">{getServiceList(lead.service_type)}</TableCell>
                  <TableCell>
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded ${getStatusBadge(lead.status)}`}>
                      {statusOptions.find(o => o.value === lead.status)?.label || lead.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">
                    {new Date(lead.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setSelectedLead(lead)}
                        data-testid={`view-lead-${lead.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => handleDelete(lead.id)}
                        data-testid={`delete-lead-${lead.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Lead Detail Modal */}
      <Dialog open={!!selectedLead} onOpenChange={() => setSelectedLead(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Archivo'] uppercase tracking-wide">
              Lead Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedLead && (
            <div className="mt-4 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="font-mono text-xs uppercase text-slate-500 mb-1">Name</p>
                  <p className="font-medium">{selectedLead.name}</p>
                </div>
                <div>
                  <p className="font-mono text-xs uppercase text-slate-500 mb-1">Status</p>
                  <Select 
                    value={selectedLead.status} 
                    onValueChange={(value) => handleStatusChange(selectedLead.id, value)}
                    disabled={isUpdating}
                  >
                    <SelectTrigger className="w-full rounded-sm" data-testid="update-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="font-mono text-xs uppercase text-slate-500 mb-1">Email</p>
                  <p>{selectedLead.email}</p>
                </div>
                <div>
                  <p className="font-mono text-xs uppercase text-slate-500 mb-1">Phone</p>
                  <p>{selectedLead.phone}</p>
                </div>
                <div>
                  <p className="font-mono text-xs uppercase text-slate-500 mb-1">Budget</p>
                  <p>{LEAD_LABELS[selectedLead.budget_range] || selectedLead.budget_range || "Not specified"}</p>
                </div>
                <div>
                  <p className="font-mono text-xs uppercase text-slate-500 mb-1">Timeline</p>
                  <p>{LEAD_LABELS[selectedLead.timeline] || selectedLead.timeline || "Not specified"}</p>
                </div>
              </div>

              <div>
                <p className="font-mono text-xs uppercase text-slate-500 mb-1">Services Needed</p>
                <p>{getServiceList(selectedLead.service_type)}</p>
              </div>

              {selectedLead.language && (
                <div>
                  <p className="font-mono text-xs uppercase text-slate-500 mb-1">Preferred Language</p>
                  <p data-testid="lead-language">{selectedLead.language === "es" ? "Spanish" : "English"}</p>
                </div>
              )}

              <div>
                <p className="font-mono text-xs uppercase text-slate-500 mb-1">Project Description</p>
                <p className="text-slate-700 whitespace-pre-wrap">{selectedLead.description}</p>
              </div>

              {selectedLead.images?.length > 0 && (
                <div>
                  <p className="font-mono text-xs uppercase text-slate-500 mb-2">Attached Images</p>
                  <div className="grid grid-cols-4 gap-2">
                    {selectedLead.images.map((img, i) => (
                      <div key={i} className="aspect-square bg-slate-100 overflow-hidden">
                        <img src={img} alt={`Attachment ${i + 1}`} className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <p className="font-mono text-xs uppercase text-slate-500 mb-1">Submitted</p>
                <p className="text-sm text-slate-600">
                  {new Date(selectedLead.created_at).toLocaleString()}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ProjectsView = ({ projects, onRefresh }) => (
  <div className="space-y-6" data-testid="projects-view">
    <div>
      <h1 className="text-2xl font-bold text-slate-900 mb-2 font-['Archivo'] uppercase">Projects</h1>
      <p className="text-slate-500">Portfolio projects ({projects.length} total)</p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {projects.map((project) => (
        <div key={project.id} className="bg-white border border-slate-200 overflow-hidden" data-testid={`project-card-${project.id}`}>
          <div className="aspect-video bg-slate-100">
            <img 
              src={project.images?.[0] || 'https://images.unsplash.com/photo-1611272267060-82338bad4112?w=400'} 
              alt={project.title}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="p-4">
            <span className="font-mono text-xs text-orange-600 uppercase">{project.category}</span>
            <h3 className="font-bold text-slate-900 mt-1 font-['Archivo']">{project.title}</h3>
            <p className="text-sm text-slate-500 mt-1">{project.location}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const TestimonialsView = ({ testimonials, onRefresh }) => (
  <div className="space-y-6" data-testid="testimonials-view">
    <div>
      <h1 className="text-2xl font-bold text-slate-900 mb-2 font-['Archivo'] uppercase">Testimonials</h1>
      <p className="text-slate-500">Customer reviews ({testimonials.length} total)</p>
    </div>

    <div className="space-y-4">
      {testimonials.map((testimonial) => (
        <div key={testimonial.id} className="bg-white border border-slate-200 p-6" data-testid={`testimonial-card-${testimonial.id}`}>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
                <span className="text-lg font-bold text-slate-600">{testimonial.client_name.charAt(0)}</span>
              </div>
              <div>
                <p className="font-bold text-slate-900">{testimonial.client_name}</p>
                {testimonial.company && <p className="text-sm text-slate-500">{testimonial.company}</p>}
                <p className="font-mono text-xs text-orange-600 uppercase mt-1">{testimonial.project_type}</p>
              </div>
            </div>
            <div className="flex">
              {[...Array(testimonial.rating)].map((_, i) => (
                <span key={i} className="text-orange-500">★</span>
              ))}
            </div>
          </div>
          <p className="text-slate-600 mt-4 italic">"{testimonial.content}"</p>
        </div>
      ))}
    </div>
  </div>
);

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [activePage, setActivePage] = useState('dashboard');
  const [stats, setStats] = useState({
    total_leads: 0,
    new_leads: 0,
    in_progress: 0,
    completed: 0,
    total_projects: 0,
    total_testimonials: 0
  });
  const [leads, setLeads] = useState([]);
  const [projects, setProjects] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }

    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [statsRes, leadsRes, projectsRes, testimonialsRes] = await Promise.all([
        adminApi.get(`${API}/admin/stats`),
        adminApi.get(`${API}/leads`),
        adminApi.get(`${API}/projects`),
        adminApi.get(`${API}/testimonials`)
      ]);

      setStats(statsRes.data);
      setLeads(leadsRes.data);
      setProjects(projectsRes.data);
      setTestimonials(testimonialsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <Sidebar activePage={activePage} setActivePage={setActivePage} />
      
      <div className="ml-64 p-8">
        {activePage === 'dashboard' && <DashboardView stats={stats} />}
        {activePage === 'leads' && <LeadsView leads={leads} onRefresh={fetchData} />}
        {activePage === 'projects' && <ProjectsView projects={projects} onRefresh={fetchData} />}
        {activePage === 'testimonials' && <TestimonialsView testimonials={testimonials} onRefresh={fetchData} />}
      </div>
    </div>
  );
}
